import { memo } from 'react';

import { createPortal } from 'react-dom';

interface I_OverlayLoadingProps {
  /** Whether the overlay is visible */
  isVisible?: boolean;
  /** Loading text to display */
  text?: string;
  /** Additional className */
  className?: string;
}

/**
 * Simple overlay loading component with centered circle loader
 * Perfect for client-side components and chart loading
 * Renders in a portal to ensure it appears above modals
 */
export const OverlayLoading = memo(function OverlayLoading({
  isVisible = false,
  text,
  className = ''
}: I_OverlayLoadingProps) {
  if (!isVisible) return null;

  const overlayContent = (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center backdrop-blur-sm bg-background/80 ${className}`}
    >
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      {text && (
        <p className="mt-4 text-sm text-muted-foreground font-medium">{text}</p>
      )}
    </div>
  );

  // Render in portal to ensure it's at the root level and above modals
  if (typeof window !== 'undefined') {
    return createPortal(overlayContent, document.body);
  }

  return overlayContent;
});

OverlayLoading.displayName = 'OverlayLoading';
